import type { AppLogger, ClientLoggerConfig } from './types'

/**
 * Creates a client-side logger with optional Sentry error reporting.
 *
 * Usage:
 * ```ts
 * import * as Sentry from '@sentry/nextjs'
 *
 * export const logger = createClientLogger({ sentry: Sentry })
 * ```
 */
export function createClientLogger(config: ClientLoggerConfig = {}): AppLogger {
    return {
        error: (message: string, errorOrContext?: unknown, context: Record<string, unknown> = {}) => {
            console.error(`[Client Error] ${message}`, errorOrContext)

            if (config.sentry) {
                const error = errorOrContext instanceof Error ? errorOrContext : undefined
                const cause = errorOrContext instanceof Error ? undefined : errorOrContext

                config.sentry.captureException(error ?? new Error(message), {
                    level: 'info',
                    tags: { type: 'client_error', feature: (context as any).feature || 'unknown' },
                    extra: {
                        message,
                        component: (context as any).component,
                        cause,
                        ...context,
                    },
                })
            }
        },

        warn: (message: string) => {
            console.warn(`[Client Warning] ${message}`)
        },

        info: (message: string) => {
            console.info(`[Client Info] ${message}`)
        },

        debug: (message: string) => {
            console.debug(`[Client Debug] ${message}`)
        },
    }
}
